import React, {useState} from "react";
import {AlertBanner, AssetsYearsInput, Button, UnderlinedHeader} from "../../components";
import {formatCurrency} from "../../utils/format";
import {QuickSlide} from '../../Goals/PortfolioReserve/QuickSlide/QuickSlide';

type InflowReserveProps = {
    isInflowWillFundLifestyleGoal: boolean,
    isProfileWithProposalsOrArchived?: boolean,
    setInflowReserveLengthInYears: (value: number) => void,
    inflowReserveLengthInYears: null | number,
    yearsOfFlow: number,
    inflowReservePresentValue: number | undefined,
    onBlur: (value: number) => void,
    portfolioReserveTargetLength: number | null;
}

export function InflowReserve(props: InflowReserveProps) {
    const [showQuickSlide, setShowQuickSlide] = useState(false);
    const [showPortfolioReserveLengthWarning, setShowPortfolioReserveLengthWarning] = useState<boolean>(props.inflowReserveLengthInYears !== null && props.portfolioReserveTargetLength !== null ?
        props.inflowReserveLengthInYears > props.portfolioReserveTargetLength
        : false);
    const [showNoPortfolioReserveTargetWarning, setShowNoPortfolioReserveTargetWarning] = useState<boolean>(props.portfolioReserveTargetLength === null);

    return <>
        {
            props.isInflowWillFundLifestyleGoal &&
            <section className="inflow-section">
                <UnderlinedHeader
                    className="inflow-reserve-section-header"
                    primaryText="Inflow Reserve"
                    secondaryContent={
                        <Button
                            icon="only"
                            iconName="info_outline"
                            kind="borderless"
                            onClick={() => {
                                setShowQuickSlide(true);
                            }}
                            size="small"
                        />
                    }
                />
                {showQuickSlide &&
                    <QuickSlide
                        title={"Inflow Reserve"}
                        imageSrc={"/InflowReserve.svg"}
                        children={<>
                            <div>
                                <p>
                                    Inflow Reserve is an allocation of Risk Control Assets within your Investable
                                    Portfolio.
                                    The purpose of this reserve is to fund your Lifestyle Spending in the event that you
                                    do not
                                    receive a particular inflow (e.g. salary). This reserve is determined by the number
                                    of
                                    Lifestyle Spending years that you want to protect.
                                </p>
                                <p>
                                    If the Inflow Reserve is not drawn upon, the Risk Control can help you fund your
                                    Portfolio Reserve.
                                </p>
                            </div>
                        </>}
                        closeHandler={() => setShowQuickSlide(false)}
                    ></QuickSlide>
                }
                <AlertBanner
                    id={'no-pr-target-warning'}
                    showAlert={showNoPortfolioReserveTargetWarning}
                    className="pr-soft-warning-text"
                    icon="info"
                    showCloseBtn={true}
                    type="info"
                    onClose={() => setShowNoPortfolioReserveTargetWarning(false)}
                    detail={
                        <span>Inflow Reserve length should not be set without a Portfolio Reserve Target.</span>}>
                </AlertBanner>
                <AlertBanner
                    id={'pr-target-length-warning'}
                    showAlert={showPortfolioReserveLengthWarning}
                    className="pr-soft-warning-text"
                    icon="info"
                    showCloseBtn={true}
                    type="info"
                    onClose={() => setShowPortfolioReserveLengthWarning(false)}
                    detail={
                        <span>Inflow Reserve length should not be greater than the Portfolio Reserve Target.</span>}>
                </AlertBanner>
                <div className="inflow-section-grid-container layout-data-entry-form__field">
                    <AssetsYearsInput
                        label="Length in Years"
                        fieldName="inflowReserveLengthInYears"
                        value={props.inflowReserveLengthInYears ?? 0}
                        maxValue={props.yearsOfFlow}
                        minValue={0}
                        maxLength={3}
                        onChangeValue={(_event, value) => {
                            props.setInflowReserveLengthInYears(value)
                            setShowPortfolioReserveLengthWarning(props.portfolioReserveTargetLength !==null ? (value > props.portfolioReserveTargetLength) : false);
                        }}
                        onBlur={(_event, value) => {
                            const editedLengthInYears = value || 0;
                            props.setInflowReserveLengthInYears(editedLengthInYears)
                            props.onBlur(editedLengthInYears)
                            setShowPortfolioReserveLengthWarning(props.portfolioReserveTargetLength !==null ? (editedLengthInYears > props.portfolioReserveTargetLength) : false);
                        }}
                        readOnly={props.isProfileWithProposalsOrArchived}
                    />
                    <span className="empty-grid-2-columns"/>
                </div>
                <div className="layout-data-entry-form__field">
                    <label id="inflow-reserve-value"><b>Inflow Reserve Value</b></label>
                    <span
                        aria-labelledby="inflow-reserve-value">{formatCurrency(props.inflowReservePresentValue)}</span>
                </div>
            </section>
        }
    </>
}